import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScannerStore } from '../../store/scanner.store';
import { useUserStore } from '../../store/user.store';
import { updateService, AppVersion } from '../../services/update.service';
import toast from 'react-hot-toast';
import pkg from '../../package.json';
import { StaffService, StaffProfile } from '../../services/staff.service';
import { supabase } from '../../services/supabase';
import { useBluetoothStore } from '../../store/bluetooth.store';
import { useThemeStore, ThemeMode } from '../../store/theme.store';
import { useTranslation } from 'react-i18next';
import { useNumberRules, useSaveNumberRule } from '../../hooks/useNumberRules';
import { NumberGenerationRule, Brick } from '../../services/number-rule.service';
import BricksEditor from './BricksEditor';

const Settings: React.FC = () => {
    const navigate = useNavigate();
    const { user, signOut } = useUserStore();
    const {
        model: pdaModel, setModel, scanAction, scanExtra, setConfig,
        reprintMode, setReprintMode,
        weightAuditAbs, setWeightAuditAbs,
        weightAuditPercent, setWeightAuditPercent,
        exportMode, setExportMode
    } = useScannerStore();
    const btState = useBluetoothStore();
    const { mode: themeMode, setMode: setThemeMode } = useThemeStore();
    const { t, i18n } = useTranslation();
    const [showPrinterModal, setShowPrinterModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showPdaModal, setShowPdaModal] = useState(false);
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);
    const [showSequenceModal, setShowSequenceModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [latestVersion, setLatestVersion] = useState<AppVersion | null>(null);
    const [checking, setChecking] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

    React.useEffect(() => {
        (window as any).onUpdateProgress = (progress: number) => {
            if (progress === -1) {
                setIsUpdating(false);
                setDownloadProgress(null);
                toast.error(t('settings.download_failed', '下载失败，请重试'));
                return;
            }
            setDownloadProgress(progress);
            if (progress === 100) {
                // Keep modal open briefly or close it as Android will pop up installer
                setTimeout(() => {
                    setIsUpdating(false);
                    setDownloadProgress(null);
                    setShowUpdateModal(false);
                }, 1000);
            }
        };
        return () => {
            delete (window as any).onUpdateProgress;
        };
    }, []);

    const currentVersion = pkg.version;

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    // Printer Config Modal
    const PrinterModal = () => (
        <div className="fixed inset-0 z-[60] bg-background-light dark:bg-background-dark flex flex-col animate-slide-up">
            <header className="bg-surface-dark/95 backdrop-blur shadow-md z-50 sticky top-0 border-b border-white/5">
                <div className="px-4 py-4 flex items-center justify-between relative">
                    <button
                        onClick={() => setShowPrinterModal(false)}
                        className="p-2 -ml-2 text-gray-400 hover:text-white rounded-full active:bg-white/10 transition-colors"
                    >
                        <span className="material-icons">close</span>
                    </button>
                    <h1 className="text-lg font-bold text-white tracking-wide">{t('settings.printer_config', '打印机配置')}</h1>
                    <div className="w-10"></div>
                </div>
            </header>
            <main className="flex-1 p-4 overflow-y-auto flex flex-col gap-5 pb-4 no-scrollbar">
                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.current_device', '当前设备')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden shadow-sm">
                        <div className="p-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-lg bg-primary/20 text-primary border border-primary/20">
                                        <span className="material-icons text-xl">print</span>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-400">{t('settings.current_printer', '当前打印机')}</div>
                                        <div className="text-base font-semibold text-white">Zebra-ZD420</div>
                                    </div>
                                </div>
                                <button className="px-3 py-1.5 rounded-lg bg-surface-hover hover:bg-white/10 text-xs font-medium text-primary border border-primary/30 transition-colors">
                                    {t('common.switch', '切换')}
                                </button>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="inline-flex items-center gap-1 text-green-500 bg-green-500/10 px-2 py-0.5 rounded">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                    {t('common.connected', '已连接')}
                                </span>
                                <span className="text-gray-500 font-mono text-[10px] pt-0.5">{t('settings.mac', 'MAC')}: 00:11:22:33:44:55</span>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="flex-1">
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden shadow-sm flex flex-col gap-0 divide-y divide-white/5">
                        <div className="p-4">
                            <label className="block text-sm font-medium text-gray-400 mb-3">{t('settings.print_size', '打印尺寸')}</label>
                            <div className="grid grid-cols-3 gap-3">
                                {(['76x130', '100x150', '100x170', '100x100', '100x130', '50x30', '40x30'] as const).map(size => (
                                    <button
                                        key={size}
                                        onClick={() => btState.setPrintSize(size)}
                                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${btState.printSize === size ? 'border-primary bg-primary/10' : 'border-white/5 bg-surface-hover/50'
                                            }`}
                                    >
                                        <span className={`material-icons-outlined mb-1 text-2xl ${btState.printSize === size ? 'text-primary' : 'text-gray-500'}`}>
                                            {size.startsWith('100') ? 'crop_landscape' : size === '40x30' || size === '50x30' ? 'style' : 'crop_portrait'}
                                        </span>
                                        <span className={`text-xs font-bold ${btState.printSize === size ? 'text-white' : 'text-gray-400'}`}>{size}</span>
                                        <span className={`text-[10px] ${btState.printSize === size ? 'text-primary/80' : 'text-gray-600'}`}>{t('settings.mm', 'mm')}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="p-4">
                            <label className="block text-sm font-medium text-gray-400 mb-3">
                                {t('settings.print_protocol', '打印协议')}
                                <span className="ml-2 text-[10px] text-gray-600 font-normal italic">(RAW Commands)</span>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {(['auto', 'tspl', 'cpcl', 'escpos'] as const).map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => btState.setPrintProtocol(p)}
                                        className={`py-2.5 rounded-lg border text-sm font-bold transition-all ${btState.printProtocol === p
                                            ? 'border-primary bg-primary/10 text-white shadow-[0_0_10px_rgba(19,91,236,0.1)]'
                                            : 'border-white/5 bg-surface-hover/30 text-gray-500'
                                            }`}
                                    >
                                        {p === 'auto' ? t('settings.auto_detect', '自动识别 (RAW)') : p.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 flex flex-col gap-4">
                            {/* Print Density */}
                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-400">{t('settings.print_density', '打印浓度')}</label>
                                    <span className="text-sm font-bold text-primary">LV {btState.printDensity}</span>
                                </div>
                                <input
                                    className="w-full h-2 bg-surface-hover rounded-lg appearance-none cursor-pointer accent-primary"
                                    type="range"
                                    min="0"
                                    max="15"
                                    value={btState.printDensity}
                                    onChange={(e) => btState.setPrintDensity(parseInt(e.target.value, 10))}
                                />
                                <div className="flex justify-between mt-1 text-[10px] text-gray-600">
                                    <span>{t('settings.light', '淡 (0)')}</span>
                                    <span>{t('settings.normal', '标准 (8)')}</span>
                                    <span>{t('settings.dark', '深 (15)')}</span>
                                </div>
                            </div>

                            {/* Gap Height */}
                            <div className="pt-2 border-t border-white/5">
                                <div className="flex justify-between mb-2 items-center">
                                    <label className="text-sm font-medium text-gray-400">{t('settings.print_gap', '走纸间距 (GAP)')}</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            className="w-16 bg-surface-hover border border-white/10 rounded px-2 py-1 text-sm text-center focus:border-primary outline-none text-white"
                                            value={btState.printGap}
                                            onChange={(e) => btState.setPrintGap(parseFloat(e.target.value) || 0)}
                                            min="0"
                                            max="10"
                                            step="0.5"
                                        />
                                        <span className="text-xs text-gray-500">mm</span>
                                    </div>
                                </div>

                                {/* Auto Detect Gap */}
                                <div className="mt-3 flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-white">{t('settings.auto_detect_gap', '智能测纸')}</span>
                                        <span className="text-[10px] text-gray-500">{t('settings.auto_detect_gap_desc', '打印前自动走纸标定间距 (TSPL)')}</span>
                                    </div>
                                    <label className="relative inline-flex cursor-pointer items-center">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={btState.printAutoDetectGap}
                                            onChange={(e) => btState.setPrintAutoDetectGap(e.target.checked)}
                                        />
                                        <div className="peer h-6 w-11 rounded-full bg-slate-700 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full outline-none"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="h-8"></div>
            </main>
            <div className="fixed bottom-0 left-0 right-0 bg-surface-dark/95 backdrop-blur border-t border-white/10 p-4 safe-area-pb z-50 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.5)]">
                <div className="flex gap-3 max-w-lg mx-auto">
                    <button className="flex-1 py-3.5 px-4 rounded-xl bg-surface-hover border border-white/10 text-gray-200 font-bold text-base hover:bg-white/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-sm">
                        <span className="material-icons text-xl">print</span>
                        {t('settings.test_print', '测试打印')}
                    </button>
                    <button
                        onClick={() => setShowPrinterModal(false)}
                        className="flex-[2] py-3.5 px-4 rounded-xl bg-primary text-white font-bold text-base hover:bg-primary-dark active:scale-[0.98] transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2"
                    >
                        <span className="material-icons text-xl">save</span>
                        {t('settings.confirm_and_save', '确认并保存')}
                    </button>
                </div>
            </div>
        </div>
    );

    // Update Check Modal
    const UpdateModal = () => {
        if (!latestVersion) return null;

        const hasNewVersion = latestVersion.version_name !== `v${currentVersion}` && latestVersion.version_name !== currentVersion;

        return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
                <div className="bg-surface-dark w-full max-w-sm rounded-xl overflow-hidden border border-white/10 shadow-2xl animate-scale">
                    <header className="p-6 text-center border-b border-white/10">
                        <h2 className="text-white text-xl font-bold">{hasNewVersion ? t('settings.new_version_found', '发现新版本') : t('settings.check_update', '检查更新')}</h2>
                    </header>
                    <main className="p-6">
                        <div className="flex flex-col items-center mb-6">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                <span className="material-icons text-primary text-3xl">
                                    {hasNewVersion ? 'rocket_launch' : 'check_circle'}
                                </span>
                            </div>
                            {hasNewVersion ? (
                                <p className="text-green-500 font-semibold text-lg">{t('settings.new_version', '新版本')} <span className="font-mono">{latestVersion.version_name}</span></p>
                            ) : (
                                <p className="text-gray-400 font-semibold text-lg">{t('settings.already_latest_version', '已是最新版本')}</p>
                            )}
                        </div>
                        <div className="space-y-4">
                            <p className="text-gray-400 text-sm">{t('settings.current_version', '当前版本')}: <span className="font-mono">v{currentVersion}</span></p>
                            {hasNewVersion && (
                                <div className="bg-background-dark/50 rounded-lg p-4 border border-white/5">
                                    <h3 className="text-white font-semibold mb-2 text-sm">{t('settings.update_content', '更新内容')}:</h3>
                                    <div className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                                        {latestVersion.changelog || t('settings.default_changelog', '优化系统体验，修复已知问题。')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </main>
                    <footer className="p-6 pt-2">
                        <div className="min-h-[56px] flex items-center justify-center">
                            {hasNewVersion ? (
                                <button
                                    onClick={() => {
                                        if ((window as any).Android?.startDownload) {
                                            setIsUpdating(true);
                                            setDownloadProgress(0);
                                            const apkName = `hedui_v${latestVersion.version_name.replace('v', '')}.apk`;
                                            (window as any).Android.startDownload(latestVersion.download_url, apkName);
                                            toast.success(t('settings.start_downloading', '开始下载新版本...'));
                                        } else {
                                            setIsUpdating(true);
                                            window.open(latestVersion.download_url, '_blank');
                                            toast.success(t('settings.opening_browser_download', '正在打开浏览器下载...'));
                                            setTimeout(() => {
                                                setIsUpdating(false);
                                                setShowUpdateModal(false);
                                            }, 3000);
                                        }
                                    }}
                                    disabled={isUpdating}
                                    className="w-full bg-primary hover:bg-primary-dark active:scale-[0.98] transition-all text-white font-bold rounded-lg py-3.5 shadow-lg flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
                                >
                                    {isUpdating ? (
                                        <div className="w-full px-4">
                                            <div className="flex justify-between text-[10px] text-white/70 font-bold mb-1.5 uppercase tracking-wider">
                                                <span>{t('settings.syncing_package', '正在同步数据包...')}</span>
                                                <span className="font-mono">{downloadProgress}%</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-white transition-all duration-300 ease-out shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                                                    style={{ width: `${downloadProgress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ) : (
                                        t('settings.update_now', '立即更新')
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowUpdateModal(false)}
                                    className="w-full bg-surface-hover hover:bg-white/10 text-white font-bold rounded-lg py-3.5 border border-white/10 active:scale-[0.98] transition-all"
                                >
                                    {t('settings.ok_got_it', '我知道了')}
                                </button>
                            )}
                        </div>
                        {hasNewVersion && !isUpdating && (
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="w-full text-gray-400 hover:text-white transition-colors py-3 text-sm font-medium mt-2"
                            >
                                {t('settings.maybe_later', '稍后再说')}
                            </button>
                        )}
                    </footer>
                </div>
            </div>
        );
    };

    // PDA Model Selection Modal
    const PdaModal = () => (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center animate-fade-in">
            <div className="bg-surface-dark w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-slide-up">
                <div className="p-5 border-b border-white/10 flex justify-between items-center bg-surface-dark sticky top-0 z-10">
                    <h3 className="text-xl font-bold text-white tracking-wide">{t('settings.pda_model_title', '选择 PDA 型号')}</h3>
                    <button onClick={() => setShowPdaModal(false)} className="p-1 rounded-full hover:bg-white/10 text-gray-400">
                        <span className="material-icons">close</span>
                    </button>
                </div>
                <div className="p-5 overflow-y-auto flex-1">
                    <div className="space-y-3">
                        <label className="relative group cursor-pointer block">
                            <input className="peer sr-only" name="pda_model" type="radio" value="NT20" checked={pdaModel === 'NT20'} onChange={() => setModel('NT20')} />
                            <div className="flex items-center p-4 rounded-xl border-2 border-transparent bg-surface-hover hover:border-white/20 transition-all peer-checked:border-primary peer-checked:bg-primary/10 peer-checked:shadow-[0_0_15px_rgba(19,91,236,0.2)]">
                                <div className="h-12 w-12 rounded-lg bg-surface-dark border border-white/10 flex items-center justify-center shrink-0 text-white mr-4">
                                    <span className="material-icons text-2xl">smartphone</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-lg font-bold text-gray-200 peer-checked:text-white">NT20</div>
                                    {pdaModel === 'NT20' && <div className="text-sm text-primary font-medium">{t('settings.current_device', '当前设备')}</div>}
                                </div>
                                <div className="h-6 w-6 rounded-full border-2 border-gray-600 peer-checked:border-primary peer-checked:bg-primary flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
                                    <span className="material-icons text-white text-sm">check</span>
                                </div>
                            </div>
                        </label>
                        <label className="relative group cursor-pointer block">
                            <input className="peer sr-only" name="pda_model" type="radio" value="DT50" checked={pdaModel === 'DT50'} onChange={() => setModel('DT50')} />
                            <div className="flex items-center p-4 rounded-xl border-2 border-transparent bg-surface-hover hover:border-white/20 transition-all peer-checked:border-primary peer-checked:bg-primary/10">
                                <div className="h-12 w-12 rounded-lg bg-background-dark/50 flex items-center justify-center shrink-0 text-gray-400 mr-4">
                                    <span className="material-icons text-2xl">ad_units</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-lg font-bold text-gray-200 peer-checked:text-white">DT50</div>
                                </div>
                                <div className="h-6 w-6 rounded-full border-2 border-gray-600 peer-checked:border-primary peer-checked:bg-primary flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
                                    <span className="material-icons text-white text-sm">check</span>
                                </div>
                            </div>
                        </label>
                        <label className="relative group cursor-pointer block">
                            <input className="peer sr-only" name="pda_model" type="radio" value="i6310" checked={pdaModel === 'i6310'} onChange={() => setModel('i6310')} />
                            <div className="flex items-center p-4 rounded-xl border-2 border-transparent bg-surface-hover hover:border-white/20 transition-all peer-checked:border-primary peer-checked:bg-primary/10">
                                <div className="h-12 w-12 rounded-lg bg-background-dark/50 flex items-center justify-center shrink-0 text-gray-400 mr-4">
                                    <span className="material-icons text-2xl">phone_android</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-lg font-bold text-gray-200 peer-checked:text-white">i6310</div>
                                </div>
                                <div className="h-6 w-6 rounded-full border-2 border-gray-600 peer-checked:border-primary peer-checked:bg-primary flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
                                    <span className="material-icons text-white text-sm">check</span>
                                </div>
                            </div>
                        </label>
                        <label className="relative group cursor-pointer block">
                            <input className="peer sr-only" name="pda_model" type="radio" value="Custom" checked={pdaModel === 'Custom'} onChange={() => setModel('Custom')} />
                            <div className="flex items-center p-4 rounded-xl border-2 border-transparent bg-surface-hover hover:border-white/20 transition-all peer-checked:border-primary peer-checked:bg-primary/10">
                                <div className="h-12 w-12 rounded-lg bg-background-dark/50 flex items-center justify-center shrink-0 text-gray-400 mr-4">
                                    <span className="material-icons text-2xl">devices_other</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-lg font-bold text-gray-200 peer-checked:text-white">{t('settings.other', '其他')} / Custom</div>
                                </div>
                                <div className="h-6 w-6 rounded-full border-2 border-gray-600 peer-checked:border-primary peer-checked:bg-primary flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
                                    <span className="material-icons text-white text-sm">check</span>
                                </div>
                            </div>
                        </label>

                        {/* Custom Configuration Fields */}
                        {pdaModel === 'Custom' && (
                            <div className="mt-4 p-4 bg-background-dark/30 rounded-xl border border-white/5 space-y-4 animate-fade-in">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{t('settings.broadcast_action', '广播意图 (Broadcast Action)')}</label>
                                    <input
                                        type="text"
                                        value={scanAction}
                                        onChange={(e) => setConfig(e.target.value, scanExtra)}
                                        className="w-full bg-surface-hover border border-white/10 rounded-lg py-2.5 px-3 text-sm text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                                        placeholder="e.g. android.intent.action.SCAN_RESULT"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{t('settings.data_extra', '数据标签 (Data Extra)')}</label>
                                    <input
                                        type="text"
                                        value={scanExtra}
                                        onChange={(e) => setConfig(scanAction, e.target.value)}
                                        className="w-full bg-surface-hover border border-white/10 rounded-lg py-2.5 px-3 text-sm text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                                        placeholder="e.g. android.intent.extra.SCAN_BROADCAST_DATA"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-500 italic">
                                    {t('settings.custom_pda_hint', '* 请根据 PDA 厂商提供的文档设置对应的广播 Action 和 Data Extra。')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-5 border-t border-white/10 bg-surface-dark sticky bottom-0 z-10 pb-8 sm:pb-5">
                    <button onClick={() => setShowPdaModal(false)} className="w-full py-3.5 mb-3 rounded-xl bg-primary text-white font-bold text-lg shadow-lg shadow-primary/30 active:scale-[0.98] transition-transform flex items-center justify-center">
                        {t('settings.confirm_selection', '确认选择')}
                    </button>
                    <button onClick={() => setShowPdaModal(false)} className="w-full py-3.5 rounded-xl bg-transparent border border-white/10 text-gray-300 font-medium text-lg hover:bg-white/5 active:scale-[0.98] transition-all">
                        {t('common.cancel', '取消')}
                    </button>
                </div>
            </div>
        </div>
    );

    // Staff Detail Modal (Permission Settings) - Screen 2 Style
    const StaffDetailModal = ({ staff, onClose, onRefresh }: { staff: StaffProfile; onClose: () => void; onRefresh: () => void }) => {
        const [isRemoving, setIsRemoving] = useState(false);

        const togglePermission = async (permission: string) => {
            let next = [...(staff.permissions || [])];
            if (next.includes(permission)) {
                next = next.filter(p => p !== permission);
            } else {
                next.push(permission);
            }
            try {
                await StaffService.updatePermissions(staff.id, next);
                toast.success(t('settings.permission_updated', '权限已更新'));
                onRefresh();
            } catch (e) {
                toast.error(t('settings.update_failed', '更新失败'));
            }
        };

        const handleRemoveStaff = async () => {
            if (!window.confirm(t('settings.remove_staff_confirm', '确定要移除员工 {{name}} 吗？\n移除后该员工将无法访问公司数据。', { name: staff.full_name }))) return;
            setIsRemoving(true);
            try {
                await StaffService.removeStaff(staff.id);
                toast.success(t('settings.staff_removed', '员工已移除'));
                onRefresh();
                onClose();
            } catch (e) {
                toast.error(t('settings.remove_failed', '移除失败'));
            } finally {
                setIsRemoving(false);
            }
        };

        return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
                <div className="relative w-full max-w-[360px] flex flex-col gap-6 overflow-hidden rounded-[24px] border border-white/10 bg-[#1e2330]/80 p-6 shadow-2xl backdrop-blur-xl animate-scale">
                    {/* Close Button */}
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                        <span className="material-icons text-[20px]">close</span>
                    </button>

                    {/* Profile Section */}
                    <div className="flex flex-col items-center gap-3 pt-2">
                        <div className="relative">
                            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 p-0.5 shadow-lg ring-2 ring-white/5 flex items-center justify-center overflow-hidden">
                                {staff.full_name?.[0] || 'U'}
                            </div>
                            <div className="absolute bottom-0 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 ring-2 ring-[#1e2330]">
                                <span className="material-icons text-white text-[14px]">check</span>
                            </div>
                        </div>
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-white tracking-tight">{staff.full_name}</h2>
                            <p className="text-sm font-medium text-gray-400 mt-1">{staff.email}</p>
                            <div className="mt-2 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-black uppercase text-primary ring-1 ring-inset ring-primary/20">
                                {staff.is_master ? 'MASTER OWNER' : 'OFFICIAL STAFF'}
                            </div>
                        </div>
                    </div>

                    <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

                    {/* Permissions List */}
                    <div className="flex flex-col gap-4">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 pl-1">{t('settings.access_control', '权限设置 (Access Control)')}</h3>

                        {[
                            { id: 'warehouse', label: t('settings.permission_warehouse', '仓库操作权限'), desc: t('settings.permission_warehouse_desc', '入库、出库与盘点'), icon: 'inventory_2', color: 'indigo' },
                            { id: 'finance', label: t('settings.permission_finance', '财务对账权限'), desc: t('settings.permission_finance_desc', '查看报表与对账'), icon: 'account_balance_wallet', color: 'emerald' },
                            { id: 'manager', label: t('settings.permission_manager', '企业管理权限'), desc: t('settings.permission_manager_desc', '人员管理与系统设置'), icon: 'admin_panel_settings', color: 'orange' }
                        ].map(p => (
                            <div key={p.id} className="group flex items-center justify-between gap-3 rounded-xl bg-white/5 p-3.5 transition-colors border border-white/5">
                                <div className="flex items-center gap-3.5">
                                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-${p.color}-500/20 text-${p.color}-400`}>
                                        <span className="material-icons text-xl">{p.icon}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-bold text-white">{p.label}</span>
                                        <span className="text-[10px] text-gray-400 font-medium">{p.desc}</span>
                                    </div>
                                </div>
                                <label className="relative inline-flex cursor-pointer items-center">
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={staff.permissions?.includes(p.id)}
                                        onChange={() => togglePermission(p.id)}
                                        disabled={staff.is_master}
                                    />
                                    <div className="peer h-6 w-11 rounded-full bg-slate-700 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full outline-none"></div>
                                </label>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 flex flex-col gap-3">
                        <button
                            onClick={onClose}
                            className="flex w-full items-center justify-center rounded-xl bg-primary py-4 text-sm font-black text-white shadow-xl shadow-primary/30 active:scale-[0.98] transition-all"
                        >
                            {t('settings.save_and_back', '保存并返回')}
                        </button>
                        {!staff.is_master && (
                            <button
                                onClick={handleRemoveStaff}
                                disabled={isRemoving}
                                className="flex w-full items-center justify-center rounded-xl bg-transparent py-2.5 text-xs font-bold text-red-500 hover:bg-red-500/10 transition-colors"
                            >
                                <span className="material-icons mr-1.5 text-base">person_remove</span>
                                {isRemoving ? t('settings.removing', '正在移除...') : t('settings.remove_member', '移除该成员')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Number Sequence Configuration Modal
    const NumberSequenceModal = () => {
        const { user } = useUserStore();
        const { data: rules, isLoading } = useNumberRules(user?.company_id);
        const saveRuleMutation = useSaveNumberRule();
        const [editingRule, setEditingRule] = useState<Partial<NumberGenerationRule> | null>(null);

        const previewNumber = (rule: Partial<NumberGenerationRule>) => {
            if (rule.generation_strategy === 'RANDOM_ALPHA') return 'ABCDEFGH';
            if (rule.generation_strategy === 'MANUAL_ASK') return 'ASK (SEQUENCE | RANDOM)';

            if (!rule.bricks || rule.bricks.length === 0) {
                // Fallback to legacy preview if no bricks
                const prefix = rule.prefix || '';
                let dateStr = rule.date_format === 'YYYYMMDD' ? new Date().toISOString().split('T')[0].replace(/-/g, '') : '';
                const seq = '1'.padStart(rule.sequence_length || 4, '0');
                let res = prefix;
                if (dateStr) {
                    res += dateStr;
                    if (!res.endsWith('-') && !res.endsWith('/')) res += '-';
                }
                return res + seq;
            }

            return rule.bricks.map(b => {
                switch (b.type) {
                    case 'text': return b.value || '';
                    case 'date': return b.format !== 'NONE' ? '20250101' : '';
                    case 'separator': return b.value || '';
                    case 'sequence': return '1'.padStart(b.length || 4, '0');
                    case 'random': return 'A'.repeat(b.length || 4);
                    case 'staff': return 'STAFF';
                    case 'company': return 'HQ';
                    default: return '';
                }
            }).join('');
        };

        const handleSave = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!editingRule) return;

            try {
                await saveRuleMutation.mutateAsync(editingRule);
                toast.success(t('settings.sequence_save_success', '配置已保存'));
                setEditingRule(null);
            } catch (error) {
                toast.error(t('settings.sequence_save_failed', '保存失败'));
            }
        };

        const isAdmin = user?.role === 'admin' || user?.is_master;
        const [activeTab, setActiveTab] = useState<'personal' | 'company'>('personal');

        const getRuleByType = (type: 'tracking_no' | 'batch_no') => {
            const rType = type as 'tracking_no' | 'batch_no';
            const match = rules?.find(r => r.type === rType && (activeTab === 'personal' ? r.user_id === user?.id : !r.user_id));
            
            if (activeTab === 'personal') {
                const companyFallback = rules?.find(r => r.type === rType && !r.user_id);
                return match || {
                    ...(companyFallback || {}),
                    id: undefined, 
                    company_id: user?.company_id || '',
                    user_id: user?.id || null, 
                    type: rType,
                    prefix: rType === 'tracking_no' ? 'WA' : 'BT',
                    date_format: 'YYYYMMDD',
                    sequence_length: 4,
                    current_value: 0
                } as Partial<NumberGenerationRule>;
            }

            return match || {
                company_id: user?.company_id || '',
                user_id: null,
                type: rType,
                prefix: rType === 'tracking_no' ? 'WA' : 'BT',
                date_format: 'YYYYMMDD',
                sequence_length: 4,
                current_value: 0
            } as Partial<NumberGenerationRule>;
        };

        return (
            <div className="fixed inset-0 z-[80] bg-[#0f172a] flex flex-col animate-slide-up">
                <header className="flex flex-col border-b border-white/5 bg-[#111827]/90 backdrop-blur-md">
                    <div className="flex items-center justify-between px-4 py-3">
                        <button onClick={() => setShowSequenceModal(false)} className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 text-white transition-colors">
                            <span className="material-icons">close</span>
                        </button>
                        <h1 className="text-lg font-black tracking-tight text-white">{t('settings.sequence_settings', '单号与批次配置')}</h1>
                        <div className="w-10"></div>
                    </div>
                    {/* Tab Navigation */}
                    <div className="flex px-4 gap-4 mt-2 border-b border-white/10 overflow-x-auto no-scrollbar">
                        <button
                            type="button"
                            onClick={() => { setActiveTab('personal'); setEditingRule(null); }}
                            className={`pb-3 px-2 text-sm font-bold whitespace-nowrap transition-colors border-b-2 ${activeTab === 'personal' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-300'}`}
                        >
                            我的专属 (Personal)
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => { setActiveTab('company'); setEditingRule(null); }}
                                className={`pb-3 px-2 text-sm font-bold whitespace-nowrap transition-colors border-b-2 ${activeTab === 'company' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-300'}`}
                            >
                                公司默认 (Company Default)
                            </button>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto no-scrollbar p-6 space-y-6">
                    {isLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <>
                            {['tracking_no', 'batch_no'].map((type) => {
                                const rule = getRuleByType(type as any);
                                const isEditing = editingRule?.type === type;
                                const currentRule = isEditing ? editingRule : rule;

                                return (
                                    <div key={type} className="bg-surface-dark rounded-2xl border border-white/5 overflow-hidden shadow-xl">
                                        <div className="p-4 border-b border-white/5 bg-white/5 flex justify-between items-center">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${type === 'tracking_no' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                                                    <span className="material-icons">{type === 'tracking_no' ? 'tag' : 'Inventory_2'}</span>
                                                </div>
                                                <span className="font-bold text-gray-200">
                                                    {type === 'tracking_no' ? t('settings.sequence_type_tracking', '运单号 (Tracking No)') : t('settings.sequence_type_batch', '批次号 (Batch No)')}
                                                </span>
                                            </div>
                                            {!isEditing && (
                                                <button
                                                    onClick={() => setEditingRule(rule)}
                                                    className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                                                >
                                                    <span className="material-icons">edit</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className="p-4 space-y-4">
                                            {isEditing ? (
                                                <form onSubmit={handleSave} className="space-y-4">
                                                    <div>
                                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1 block">{t('settings.sequence_gen_mode', '生成模式')}</label>
                                                        <select
                                                            className="w-full bg-[#1e293b] border border-white/5 rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-primary outline-none appearance-none mb-4"
                                                            value={editingRule!.generation_strategy || 'SEQUENCE'}
                                                            onChange={e => setEditingRule({ ...editingRule!, generation_strategy: e.target.value as any })}
                                                        >
                                                            <option value="SEQUENCE">{t('settings.sequence_gen_mode_sequence', '规则递增 / 规则块配置')}</option>
                                                            <option value="RANDOM_ALPHA">{t('settings.sequence_gen_mode_random', '全随机字母 (8位)')}</option>
                                                            <option value="MANUAL_ASK">{t('settings.sequence_gen_mode_ask', '人工询问')}</option>
                                                        </select>
                                                    </div>

                                                    {(editingRule!.generation_strategy === 'SEQUENCE' || !editingRule!.generation_strategy) && (
                                                        <BricksEditor
                                                            bricks={editingRule!.bricks || []}
                                                            onChange={newBricks => setEditingRule({ ...editingRule!, bricks: newBricks })}
                                                        />
                                                    )}

                                                    <div className="pt-2 flex gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingRule(null)}
                                                            className="flex-1 py-2 rounded-xl bg-white/5 text-gray-400 font-bold text-sm"
                                                        >
                                                            {t('common.cancel', '取消')}
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="flex-1 py-2 rounded-xl bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20"
                                                        >
                                                            {t('common.save', '保存')}
                                                        </button>
                                                    </div>
                                                </form>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-gray-500">{t('settings.sequence_preview', '预览')}</span>
                                                        <span className="font-mono text-primary font-bold">{previewNumber(rule)}</span>
                                                    </div>
                                                    <div className="flex gap-4 text-[11px] text-gray-500 border-t border-white/5 pt-3">
                                                        <span>{t('settings.sequence_prefix', '前缀')}: {rule.prefix}</span>
                                                        <span>{t('settings.sequence_length', '长度')}: {rule.sequence_length}</span>
                                                        <span>{t('settings.sequence_date_format', '日期')}: {rule.date_format}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </main>
            </div >
        );
    };

    // Add Staff Directly Modal - Generated from Stitch UI "create for employee"
    const AddStaffModal = ({ onClose, onRefresh }: { onClose: () => void; onRefresh: () => void }) => {
        const { user } = useUserStore();
        const [mode, setMode] = useState<'invite' | 'direct'>('invite');
        const [copied, setCopied] = useState(false);
        const [regUrl, setRegUrl] = useState('');
        
        // Direct creation form state
        const [form, setForm] = useState({
            email: '',
            password: '',
            full_name: '',
            role: 'sender'
        });
        const [creating, setCreating] = useState(false);

        React.useEffect(() => {
            if (user?.company?.code) {
                StaffService.getRegistrationUrl(user.company.code).then(setRegUrl);
            }
        }, [user]);

        const handleDirectCreate = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!user?.company_id) return;
            if (!form.email || !form.password || !form.full_name) {
                toast.error(t('settings.fill_all_fields', '请填写所有必填字段'));
                return;
            }

            setCreating(true);
            try {
                await StaffService.createStaffDirectly({
                    ...form,
                    company_id: user.company_id
                });
                toast.success(t('settings.staff_created', '员工账号已成功创建'));
                onRefresh();
                onClose();
            } catch (error: any) {
                toast.error(error.message || t('settings.create_failed', '创建失败'));
            } finally {
                setCreating(false);
            }
        };

        const copyInviteLink = () => {
            if (!regUrl) return;
            navigator.clipboard.writeText(regUrl);
            setCopied(true);
            toast.success(t('settings.invite_link_copied', '注册链接已复制'));
            setTimeout(() => setCopied(false), 2000);
        };

        const copyInviteFullText = () => {
            if (!user?.company?.code || !regUrl) return;
            const text = StaffService.getInviteText(
                user.company.name || '越通物流',
                user.company.code,
                regUrl
            );
            navigator.clipboard.writeText(text);
            toast.success(t('settings.invite_text_copied', '完整邀请信息已复制'));
        };

        return (
            <div className="fixed inset-0 z-[80] bg-[#0f172a] flex flex-col animate-slide-up">
                <header className="flex items-center justify-between px-4 py-3 bg-[#111827]/90 backdrop-blur-md border-b border-white/5 shrink-0">
                    <button onClick={onClose} className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 text-white transition-colors">
                        <span className="material-icons">close</span>
                    </button>
                    <h1 className="text-lg font-black tracking-tight text-white">
                        {mode === 'invite' ? t('settings.invite_staff', '邀请员工') : t('settings.add_direct', '直接添加')}
                    </h1>
                    <div className="w-10"></div>
                </header>

                {/* Mode Selector */}
                <div className="p-4 bg-[#111827]/30 border-b border-white/5 px-6">
                    <div className="bg-[#1e293b] p-1 rounded-xl flex">
                        <button
                            onClick={() => setMode('invite')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black transition-all ${mode === 'invite' ? 'bg-primary text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                            <span className="material-icons text-sm">link</span>
                            {t('settings.invite_mode', '链接邀请')}
                        </button>
                        <button
                            onClick={() => setMode('direct')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black transition-all ${mode === 'direct' ? 'bg-primary text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                            <span className="material-icons text-sm">person_add</span>
                            {t('settings.direct_mode', '直接创建')}
                        </button>
                    </div>
                </div>

                <main className="flex-1 overflow-y-auto no-scrollbar p-6 space-y-8 pb-32">
                    {mode === 'invite' ? (
                        <>
                            <div className="flex flex-col items-center text-center space-y-4 pt-4">
                                <div className="w-20 h-20 bg-primary/20 rounded-3xl flex items-center justify-center">
                                    <span className="material-icons text-primary text-5xl">person_add</span>
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-xl font-bold text-white">{t('settings.how_to_invite', '如何邀请新员工？')}</h2>
                                    <p className="text-slate-500 text-sm px-8">{t('settings.invite_desc', '出于安全考虑，推荐通过邀请链接由员工自主注册。')}</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Step 1: Registration Link */}
                                <div className="bg-[#1e293b] rounded-2xl p-5 border border-white/5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-black">1</div>
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('settings.step_send_link', '第一步：发送注册链接')}</span>
                                    </div>
                                    <div className="bg-[#0f172a] rounded-xl p-4 font-mono text-xs text-slate-500 break-all border border-white/5">
                                        {regUrl || 'Loading...'}
                                    </div>
                                    <button
                                        onClick={copyInviteLink}
                                        className="w-full h-12 bg-primary/10 hover:bg-primary/20 text-primary font-bold rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                    >
                                        <span className="material-icons text-sm">{copied ? 'check' : 'content_copy'}</span>
                                        {copied ? t('common.copied', '已复制') : t('settings.copy_link', '复制链接')}
                                    </button>
                                </div>

                                {/* Step 2: Company Code */}
                                <div className="bg-[#1e293b] rounded-2xl p-5 border border-white/5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-black">2</div>
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('settings.step_send_code', '第二步：提供验证码')}</span>
                                    </div>
                                    <div className="flex items-center justify-center bg-[#0f172a] rounded-xl p-6 border border-white/10">
                                        <span className="text-4xl font-mono font-black text-white tracking-[0.2em]">{user?.company?.code || '------'}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 text-center">{t('settings.code_required_hint', '员工注册时必须输入此代码才能关联到您的公司')}</p>
                                </div>

                                {/* Step 3: Approval */}
                                <div className="bg-[#1e293b] rounded-2xl p-5 border border-white/5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-black">3</div>
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('settings.step_approve', '第三步：开通权限')}</span>
                                    </div>
                                    <div className="flex items-center gap-4 bg-primary/5 rounded-xl p-4 border border-primary/10">
                                        <span className="material-icons text-primary/60">info_outline</span>
                                        <p className="text-xs text-slate-400 leading-relaxed">
                                            {t('settings.approval_hint', '员工注册完毕后，您将在“团队管理”列表中看到他们。点击员工姓名即可为其分配合适的职能权限。')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleDirectCreate} className="space-y-6">
                            <div className="bg-[#1e293b] rounded-2xl p-6 border border-white/5 space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('settings.full_name', '姓名')}</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="例如：张三"
                                        className="w-full bg-[#0f172a] border-none rounded-xl py-4 px-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-primary transition-all"
                                        value={form.full_name}
                                        onChange={e => setForm({...form, full_name: e.target.value})}
                                    />
                                </div>
                                
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('settings.email', '电子邮箱 (登录账号)')}</label>
                                    <input
                                        type="email"
                                        required
                                        placeholder="zhangsan@example.com"
                                        className="w-full bg-[#0f172a] border-none rounded-xl py-4 px-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-primary transition-all"
                                        value={form.email}
                                        onChange={e => setForm({...form, email: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('settings.password', '初始密码')}</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="建议8位以上数字或字母"
                                        className="w-full bg-[#0f172a] border-none rounded-xl py-4 px-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-primary transition-all font-mono"
                                        value={form.password}
                                        onChange={e => setForm({...form, password: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('settings.role_select', '初始角色')}</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {['sender', 'transit', 'receiver'].map(r => (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => setForm({...form, role: r})}
                                                className={`py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all border ${form.role === r ? 'bg-primary/20 text-primary border-primary' : 'bg-slate-800 text-slate-500 border-white/5'}`}
                                            >
                                                {t(`roles.${r}`)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex gap-4">
                                <span className="material-icons text-amber-500">warning</span>
                                <div className="space-y-1">
                                    <p className="text-[11px] font-black text-amber-500 uppercase tracking-widest">直接创建提示</p>
                                    <p className="text-xs text-amber-500/70 leading-relaxed">系统将立即为您开通此账号，初始状态包含所选角色权限。创建后请告知员工邮箱及初始密码。</p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={creating}
                                className={`w-full py-4 rounded-2xl font-black text-base transition-all flex items-center justify-center gap-2 shadow-2xl ${creating ? 'bg-slate-700 text-slate-400' : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-emerald-500/20 active:scale-95'}`}
                            >
                                <span className="material-icons">{creating ? 'refresh' : 'person_add'}</span>
                                {creating ? t('settings.creating_account', '后台处理中...') : t('settings.confirm_add', '立即创建并关联到公司')}
                            </button>
                        </form>
                    )}
                </main>

                {mode === 'invite' && (
                    <div className="fixed bottom-0 left-0 right-0 bg-[#0f172a]/90 backdrop-blur-xl border-t border-white/5 p-6 z-[90]">
                        <button
                            onClick={copyInviteFullText}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-primary to-blue-600 text-white font-black text-base shadow-2xl shadow-primary/40 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group overflow-hidden"
                        >
                            <span className="material-icons">send</span>
                            {t('settings.copy_full_invitation', '复制完整邀请信息')}
                            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-[-20deg]"></div>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Staff Management Modal - Screen 1 Style
    const StaffManagementModal = () => {
        const { user } = useUserStore();
        const [staff, setStaff] = React.useState<StaffProfile[]>([]);
        const [loading, setLoading] = React.useState(true);
        const [search, setSearch] = React.useState('');
        const [selectedStaff, setSelectedStaff] = React.useState<StaffProfile | null>(null);

        const fetchStaff = async () => {
            if (!user?.company_id) return;
            setLoading(true);
            try {
                const data = await StaffService.getStaff(user.company_id);
                setStaff(data);
                // Update selected staff if it's currently open
                if (selectedStaff) {
                    const updated = data.find(s => s.id === selectedStaff.id);
                    if (updated) setSelectedStaff(updated);
                }
            } catch (e) {
                toast.error(t('settings.fetch_staff_failed', '获取员工列表失败'));
            } finally {
                setLoading(false);
            }
        };

        React.useEffect(() => {
            fetchStaff();
        }, []);

        const filteredStaff = staff.filter(s =>
            s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
            s.email?.toLowerCase().includes(search.toLowerCase())
        );

        const copyToClipboard = (text: string) => {
            if (!text) return;
            navigator.clipboard.writeText(text);
            toast.success(t('settings.invite_code_copied', '邀请码已复制'));
        };

        return (
            <div className="fixed inset-0 z-[60] bg-[#0f172a] flex flex-col animate-slide-up">
                <header className="flex items-center justify-between px-4 py-3 bg-[#111827]/90 backdrop-blur-md sticky top-0 z-50 border-b border-white/5">
                    <button onClick={() => setShowStaffModal(false)} className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 text-white transition-colors">
                        <span className="material-icons">arrow_back_ios_new</span>
                    </button>
                    <h1 className="text-lg font-black tracking-tight text-white">{t('settings.team_management', '团队管理')}</h1>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setShowAddStaffModal(true)}
                            className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 text-primary transition-colors"
                        >
                            <span className="material-icons">person_add_alt_1</span>
                        </button>
                        <button onClick={fetchStaff} className="flex items-center justify-center p-2 rounded-full hover:bg-white/10 text-white transition-colors">
                            <span className="material-icons">refresh</span>
                        </button>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto no-scrollbar pb-4">
                    {/* Screen 1 Company Card */}
                    <div className="p-4">
                        <div className="bg-[#1e293b] rounded-2xl p-5 border border-white/5 shadow-2xl relative overflow-hidden group">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl group-hover:bg-primary/20 transition-all duration-500"></div>
                            <div className="relative z-10">
                                <div className="flex flex-col gap-1 mb-4">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-primary" style={{ fontSize: '20px' }}>domain</span>
                                        <h2 className="text-lg font-black text-white">{user?.company?.name || t('settings.default_company_name', '越通物流')}</h2>
                                    </div>
                                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest ml-7 opacity-60">({t(`roles.${user?.role || 'transit'}`)})</p>
                                </div>
                                <div className="flex flex-col gap-3 bg-[#0f172a]/50 rounded-xl p-4 border border-white/5 backdrop-blur-sm">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mb-1">{t('settings.join_code', '团队邀请码 (JOIN CODE)')}</span>
                                        <span className="text-2xl font-mono font-black text-white tracking-[0.2em]">{user?.company?.code || 'HT-8890'}</span>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(user?.company?.code || '')}
                                        className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 active:scale-95 text-white w-full py-3.5 rounded-xl text-sm font-black transition-all shadow-xl shadow-primary/20"
                                    >
                                        <span className="material-icons" style={{ fontSize: '18px' }}>content_copy</span>
                                        <span>{t('settings.copy_invite_code', '复制邀请码')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="px-4 pb-2 sticky top-0 z-30 bg-[#0f172a]/95 backdrop-blur-sm pt-2 -mt-2">
                        <div className="relative group">
                            <span className="material-icons absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary transition-colors text-lg">search</span>
                            <input
                                className="block w-full pl-12 pr-4 py-4 border-none rounded-2xl bg-[#1e293b] text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary transition-all shadow-inner"
                                placeholder={t('settings.search_staff_placeholder', '搜索员工姓名或邮箱...')}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="flex justify-between items-center mt-4 mb-2 px-1">
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('settings.team_resources', '团队资源')} ({loading ? '...' : filteredStaff.length})</h3>
                            <span className="text-[10px] text-primary font-black flex items-center gap-1 uppercase tracking-widest cursor-pointer">
                                <span className="material-icons" style={{ fontSize: '14px' }}>filter_list</span>
                                {t('settings.filter', '筛选')}
                            </span>
                        </div>
                    </div>

                    {/* Staff List */}
                    <div className="px-4 space-y-3 pb-4">
                        {loading ? (
                            <div className="py-12 flex flex-col items-center gap-4">
                                <span className="material-icons animate-spin text-4xl text-primary/30">refresh</span>
                                <span className="text-xs font-bold text-slate-600 uppercase tracking-widest">{t('settings.syncing', '同步中...')}</span>
                            </div>
                        ) : filteredStaff.length === 0 ? (
                            <div className="py-12 text-center opacity-30">
                                <span className="material-icons text-6xl block mb-2">person_off</span>
                                <p className="text-sm font-bold uppercase tracking-widest">{t('settings.no_records', '暂无记录')}</p>
                            </div>
                        ) : filteredStaff.map(s => (
                            <div
                                key={s.id}
                                onClick={() => setSelectedStaff(s)}
                                className="bg-[#1e293b] hover:bg-[#1e293b]/80 rounded-2xl p-4 flex items-center gap-4 transition-all border border-white/5 cursor-pointer group active:scale-[0.98]"
                            >
                                <div className="relative shrink-0">
                                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center font-black text-white text-xl border-2 border-[#1e293b] group-hover:border-primary/50 transition-colors shadow-lg">
                                        {s.full_name?.[0] || 'U'}
                                    </div>
                                    <div className={`absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-[#1e293b] ${s.is_master ? 'bg-primary' : 'bg-green-500'}`}></div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <p className="text-white text-base font-black truncate">{s.full_name}</p>
                                        {s.is_master && (
                                            <span className="bg-primary/20 text-primary text-[9px] px-2 py-0.5 rounded-full font-black border border-primary/20 uppercase tracking-tighter">{t('settings.master', 'MASTER')}</span>
                                        )}
                                    </div>
                                    <p className="text-slate-400 text-[11px] font-medium truncate mb-2 opacity-60 italic">{s.email}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {s.permissions?.includes('warehouse') && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-slate-800 text-slate-400 border border-white/5">{t('settings.warehouse_short', '仓管')}</span>
                                        )}
                                        {s.permissions?.includes('finance') && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-slate-800 text-slate-400 border border-white/5">{t('settings.finance_short', '财务')}</span>
                                        )}
                                        {s.permissions?.includes('manager') && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-primary/20 text-primary border border-primary/10">{t('settings.manager_short', '主管')}</span>
                                        )}
                                        {(!s.permissions || s.permissions.length === 0) && !s.is_master && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-slate-800/50 text-slate-600 border border-white/5 italic">{t('settings.no_permissions_assigned', '未指派权限')}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="shrink-0 text-slate-600 group-hover:text-primary transition-colors">
                                    <span className="material-icons">chevron_right</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </main>

                {/* Bottom Bar */}
                <div className="bg-[#111827]/90 backdrop-blur-xl border-t border-white/5 pb-8 pt-3 z-50 fixed bottom-0 w-full px-6">
                    <button
                        onClick={() => setShowStaffModal(false)}
                        className="w-full py-4 rounded-2xl bg-white/5 text-white/50 text-sm font-black uppercase tracking-[0.2em] border border-white/5 active:scale-95 transition-all"
                    >
                        {t('common.close_console', '关闭控制台')}
                    </button>
                </div>

                {/* Detail Modal layer */}
                {selectedStaff && (
                    <StaffDetailModal
                        staff={selectedStaff}
                        onClose={() => setSelectedStaff(null)}
                        onRefresh={fetchStaff}
                    />
                )}

                {/* Add Staff Modal layer */}
                {showAddStaffModal && (
                    <AddStaffModal
                        onClose={() => setShowAddStaffModal(false)}
                        onRefresh={fetchStaff}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
            <header className="bg-surface-dark/95 backdrop-blur shadow-md z-50 sticky top-0 flex-shrink-0">
                <div className="px-4 py-4 flex items-center justify-between relative border-b border-white/5">
                    <button onClick={() => navigate('/')} className="p-2 -ml-2 text-gray-400 hover:text-white">
                        <span className="material-icons">arrow_back_ios</span>
                    </button>
                    <h1 className="text-lg font-bold text-white tracking-wide ml-8">{t('settings.title', '设置')}</h1>
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-500">
                        <span
                            onClick={() => i18n.changeLanguage('zh')}
                            className={`${i18n.language.startsWith('zh') ? 'text-primary' : ''} cursor-pointer hover:text-white transition-colors`}
                        >CN</span>
                        <span className="text-white/20">|</span>
                        <span
                            onClick={() => i18n.changeLanguage('vi')}
                            className={`${i18n.language === 'vi' ? 'text-primary' : ''} cursor-pointer hover:text-white transition-colors`}
                        >VN</span>
                        <span className="text-white/20">|</span>
                        <span
                            onClick={() => i18n.changeLanguage('th')}
                            className={`${i18n.language === 'th' ? 'text-primary' : ''} cursor-pointer hover:text-white transition-colors`}
                        >TH</span>
                        <span className="text-white/20">|</span>
                        <span
                            onClick={() => i18n.changeLanguage('mm')}
                            className={`${i18n.language === 'mm' ? 'text-primary' : ''} cursor-pointer hover:text-white transition-colors`}
                        >MM</span>
                    </div>
                </div>
            </header>

            <main className="flex-1 p-4 overflow-y-auto flex flex-col gap-6 pb-4 no-scrollbar">
                {(user?.is_master || user?.role === 'admin') && (
                    <section>
                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.company_management', '公司管理')}</h2>
                        <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                            <button
                                onClick={() => setShowStaffModal(true)}
                                className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                                        <span className="material-icons">manage_accounts</span>
                                    </div>
                                    <span className="text-base font-medium text-gray-200">{t('settings.staff_management', '员工账号与权限管理')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400">{t('common.manage', '管理')}</span>
                                    <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                                </div>
                            </button>
                            <button
                                onClick={() => setShowSequenceModal(true)}
                                className="w-full flex items-center justify-between p-4 border-t border-white/5 active:bg-surface-hover transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400">
                                        <span className="material-icons">format_list_numbered</span>
                                    </div>
                                    <div className="text-left">
                                        <span className="text-base font-medium text-gray-200 block">{t('settings.sequence_settings', '单号与批次配置')}</span>
                                        <span className="text-[10px] text-gray-500">{t('settings.sequence_settings_desc', '自定义运单号与批次号生成规则')}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400 font-medium">{t('common.setup', '设置')}</span>
                                    <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                                </div>
                            </button>
                        </div>
                    </section>
                )}

                {/* ─── 收货人管理 (Sender only) ─── */}
                {(user?.role === 'sender' || user?.is_master) && (
                    <section>
                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.shipment_tools', '发货工具')}</h2>
                        <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                            <button
                                onClick={() => navigate('/settings/contacts')}
                                className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                                        <span className="material-icons">contacts</span>
                                    </div>
                                    <div className="text-left">
                                        <span className="text-base font-medium text-gray-200 block">{t('settings.contact_management', '收货人管理')}</span>
                                        <span className="text-[10px] text-gray-500">{t('settings.contact_management_desc', '常用收件人地址簿')}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">{t('settings.address_book', '地址簿')}</span>
                                    <span className="material-icons text-gray-500 text-sm group-hover:text-cyan-400 transition-colors">chevron_right</span>
                                </div>
                            </button>
                        </div>
                    </section>
                )}

                {/* ─── 消息通知设置 (Sender only) ─── */}
                {(user?.role === 'sender' || user?.is_master) && (
                    <section>
                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.notification_settings', '消息通知设置')}</h2>
                        <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                            <button
                                onClick={() => navigate('/settings/whatsapp')}
                                className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" className="w-6 h-6" alt="WA" />
                                    </div>
                                    <div className="text-left">
                                        <span className="text-base font-medium text-gray-200 block">{t('settings.whatsapp_settings', 'WhatsApp 通知设置')}</span>
                                        <span className="text-[10px] text-gray-500">{t('settings.whatsapp_desc', '扫码连接通知机器人')}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full border border-green-500/20 uppercase">{t('common.setup', '设置')}</span>
                                    <span className="material-icons text-gray-500 text-sm group-hover:text-green-500 transition-colors">chevron_right</span>
                                </div>
                            </button>
                        </div>
                    </section>
                )}

                {/* ─── Wallet Section ─── */}
                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.finance_wallet', '财务钱包')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                        <button
                            onClick={() => navigate('/wallet')}
                            className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 text-primary border border-primary/10 group-hover:shadow-lg group-hover:shadow-primary/10 transition-all">
                                    <span className="material-icons text-xl">account_balance_wallet</span>
                                </div>
                                <div className="text-left">
                                    <span className="text-base font-bold text-gray-200 block">{t('settings.my_wallet', '我的钱包')}</span>
                                    <span className="text-[10px] font-medium text-gray-500">{t('settings.my_wallet_desc', '查看批次账单、余额与流水')}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20 uppercase">
                                    {user?.role === 'sender' ? 'Bill A+C' : user?.role === 'transit' ? 'Bill B' : user?.role === 'receiver' ? 'Bill C' : 'ALL'}
                                </span>
                                <span className="material-icons text-gray-500 text-sm group-hover:text-primary transition-colors">chevron_right</span>
                            </div>
                        </button>
                    </div>
                </section>

                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.connection_settings', '连接设置')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                        <button
                            onClick={() => navigate('/settings/bluetooth')}
                            className="w-full flex items-center justify-between p-4 border-b border-white/5 active:bg-surface-hover transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10 text-primary">
                                    <span className="material-icons">monitor_weight</span>
                                </div>
                                <div>
                                    <span className="text-base font-medium text-gray-200 block">{t('settings.bluetooth_scale', '蓝牙电子秤')}</span>
                                    {btState.scaleDevice && (
                                        <span className="text-[10px] text-gray-500">{btState.scaleDevice.name}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {btState.scaleDevice ? (
                                    <span className="text-sm font-medium text-green-500 flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                        {t('common.connected', '已连接')}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-500">{t('common.not_connected', '未连接')}</span>
                                )}
                                <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                            </div>
                        </button>
                        <button
                            onClick={() => navigate('/settings/bluetooth')}
                            className="w-full flex items-center justify-between p-4 border-b border-white/5 active:bg-surface-hover transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-orange-500/10 text-orange-500">
                                    <span className="material-icons">print</span>
                                </div>
                                <div>
                                    <span className="text-base font-medium text-gray-200 block">{t('settings.bluetooth_printer', '蓝牙标签打印机')}</span>
                                    {btState.printerDevice && (
                                        <span className="text-[10px] text-gray-500">{btState.printerDevice.name}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {btState.printerDevice ? (
                                    <span className="text-sm font-medium text-green-500 flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                        {t('common.connected', '已连接')}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-500">{t('common.not_connected', '未连接')}</span>
                                )}
                                <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                            </div>
                        </button>
                        <button
                            onClick={() => navigate('/settings/label-templates')}
                            className="w-full flex items-center justify-between p-4 border-b border-white/5 active:bg-surface-hover transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                                    <span className="material-icons">label</span>
                                </div>
                                <span className="text-base font-medium text-gray-200">{t('settings.label_template_management', '标签模板管理')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">{t('common.visual_edit', '可视化编辑')}</span>
                                <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                            </div>
                        </button>
                        <button
                            onClick={() => navigate('/settings/bartender')}
                            className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-teal-500/10 text-teal-500">
                                    <span className="material-icons">wysiwyg</span>
                                </div>
                                <span className="text-base font-medium text-gray-200">{t('settings.bartender_config', 'BarTender 服务配置')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">{t('common.cloud_template', '云端模板')}</span>
                                <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                            </div>
                        </button>
                    </div>
                </section>



                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.scan_config', '扫码配置')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">
                        <div className="flex flex-col p-4 border-b border-white/5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                                        <span className="material-icons">qr_code_scanner</span>
                                    </div>
                                    <span className="text-base font-medium text-gray-200">{t('settings.scan_mode', '扫码模式')}</span>
                                </div>
                                <button
                                    onClick={() => setShowPdaModal(true)}
                                    className="text-xs text-primary hover:text-primary-light transition-colors border border-primary/30 px-2 py-1 rounded hover:bg-primary/10"
                                >
                                    {t('settings.pda_model_settings', 'PDA型号设置')}
                                </button>
                            </div>
                            <div className="flex bg-background-dark p-1 rounded-lg">
                                <button className="flex-1 py-2 rounded text-sm font-medium bg-surface-hover text-white shadow text-center transition-all">{t('settings.broadcast_mode', '广播模式')}</button>
                                <button className="flex-1 py-2 rounded text-sm font-medium text-gray-500 hover:text-gray-300 text-center transition-all">{t('settings.keyboard_input', '键盘输入')}</button>
                            </div>
                        </div>
                        <div className="flex flex-col p-4 border-b border-white/5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                                        <span className="material-icons">print</span>
                                    </div>
                                    <span className="text-base font-medium text-gray-200">{t('settings.reprint_mode', 'Re-print 模式')}</span>
                                </div>
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{t('settings.reprint_mode_hint', '扫描已存在单号时')}</span>
                            </div>
                            <div className="flex bg-background-dark p-1 rounded-lg">
                                <button
                                    onClick={() => setReprintMode('copy')}
                                    className={`flex-1 py-2 rounded text-sm font-medium transition-all ${reprintMode === 'copy' ? 'bg-surface-hover text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    {t('settings.fast_copy', '快速复制')}
                                </button>
                                <button
                                    onClick={() => setReprintMode('update')}
                                    className={`flex-1 py-2 rounded text-sm font-medium transition-all ${reprintMode === 'update' ? 'bg-surface-hover text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    {t('settings.weight_update', '称重更新')}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col p-4 border-b border-white/5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-orange-500/10 text-orange-500">
                                        <span className="material-icons">balance</span>
                                    </div>
                                    <span className="text-base font-medium text-gray-200">{t('settings.weight_audit', '重量差异预警 (Audit)')}</span>
                                </div>
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{t('settings.three_way_audit', '三方重量审计')}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="bg-background-dark p-3 rounded-xl border border-white/5">
                                    <label className="text-[10px] text-gray-500 font-bold uppercase block mb-1">{t('settings.abs_value', '绝对值 (KG)')}</label>
                                    <input
                                        type="number"
                                        value={weightAuditAbs}
                                        onChange={(e) => setWeightAuditAbs(parseFloat(e.target.value) || 0)}
                                        className="w-full bg-transparent text-white font-bold outline-none"
                                    />
                                </div>
                                <div className="bg-background-dark p-3 rounded-xl border border-white/5">
                                    <label className="text-[10px] text-gray-500 font-bold uppercase block mb-1">{t('settings.percent_value', '百分比 (%)')}</label>
                                    <input
                                        type="number"
                                        value={weightAuditPercent}
                                        onChange={(e) => setWeightAuditPercent(parseFloat(e.target.value) || 0)}
                                        className="w-full bg-transparent text-white font-bold outline-none"
                                    />
                                </div>
                            </div>
                            <div className="flex bg-background-dark p-1 rounded-lg">
                                <button
                                    onClick={() => setExportMode('anomaly')}
                                    className={`flex-1 py-2 rounded text-sm font-medium transition-all ${exportMode === 'anomaly' ? 'bg-orange-600 text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    {t('settings.export_anomaly', '仅导出异常单')}
                                </button>
                                <button
                                    onClick={() => setExportMode('all')}
                                    className={`flex-1 py-2 rounded text-sm font-medium transition-all ${exportMode === 'all' ? 'bg-orange-600 text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    {t('settings.export_all', '全量数据标记')}
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-600 mt-2 px-1">{t('settings.audit_hint', '满足任一阈值即触发报警。红色单号代表差异异常。')}</p>
                        </div>
                        <div className="flex items-center justify-between p-4 border-b border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-surface-hover text-gray-400">
                                    <span className="material-icons">volume_up</span>
                                </div>
                                <span className="text-base font-medium text-gray-200">{t('settings.prompt_language', '提示音语言')}</span>
                            </div>
                            <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in">
                                <input defaultChecked type="checkbox" name="toggle" id="toggle-sound" className="absolute block w-6 h-6 rounded-full bg-white border-4 border-surface-dark appearance-none cursor-pointer transition-all duration-300 left-0 checked:right-0 checked:border-primary checked:translate-x-full peer" />
                                <label htmlFor="toggle-sound" className="block overflow-hidden h-6 rounded-full bg-surface-hover cursor-pointer border border-white/5 peer-checked:bg-primary"></label>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-surface-hover text-gray-400">
                                    <span className="material-icons">vibration</span>
                                </div>
                                <span className="text-base font-medium text-gray-200">{t('settings.vibration_feedback', '震动反馈')}</span>
                            </div>
                            <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in">
                                <input defaultChecked type="checkbox" name="toggle" id="toggle-vibrate" className="absolute block w-6 h-6 rounded-full bg-white border-4 border-surface-dark appearance-none cursor-pointer transition-all duration-300 left-0 checked:right-0 checked:border-primary checked:translate-x-full peer" />
                                <label htmlFor="toggle-vibrate" className="block overflow-hidden h-6 rounded-full bg-surface-hover cursor-pointer border border-white/5 peer-checked:bg-primary"></label>
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.system', '系统')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden">

                        {/* ── 主题切换 ── */}
                        {/* ── 显示主题 ── */}
                        <div className="flex items-center justify-between p-4 border-b border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400">
                                    <span className="material-icons">
                                        {themeMode === 'dark' ? 'dark_mode' : themeMode === 'light' ? 'light_mode' : 'brightness_auto'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-base font-medium text-gray-200 block">{t('settings.theme', '显示主题')}</span>
                                    <span className="text-[10px] text-gray-500">
                                        {themeMode === 'system' ? t('settings.theme_system', '跟随系统') : themeMode === 'dark' ? t('settings.theme_dark', '暗色模式') : t('settings.theme_light', '亮色模式')}
                                    </span>
                                </div>
                            </div>
                            <div className="flex bg-background-dark p-1 rounded-xl border border-white/5 gap-0.5">
                                {([
                                    { value: 'system' as ThemeMode, label: t('settings.auto', '自动'), icon: 'brightness_auto' },
                                    { value: 'light' as ThemeMode, label: t('settings.light', '亮'), icon: 'light_mode' },
                                    { value: 'dark' as ThemeMode, label: t('settings.dark', '暗'), icon: 'dark_mode' },
                                ] as const).map(t => (
                                    <button
                                        key={t.value}
                                        onClick={() => setThemeMode(t.value)}
                                        title={t.label}
                                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 ${themeMode === t.value
                                            ? 'bg-indigo-500 text-white shadow'
                                            : 'text-gray-500 hover:text-gray-300'
                                            }`}
                                    >
                                        <span className="material-icons text-[14px]">{t.icon}</span>
                                        <span className="hidden sm:inline">{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 border-b border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500">
                                    <span className="material-icons">wb_sunny</span>
                                </div>
                                <span className="text-base font-medium text-gray-200">{t('settings.screen_always_on', '屏幕常亮')}</span>
                            </div>
                            <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in">
                                <input type="checkbox" name="toggle" id="toggle-screen" className="absolute block w-6 h-6 rounded-full bg-white border-4 border-surface-dark appearance-none cursor-pointer transition-all duration-300 left-0 checked:right-0 checked:border-primary checked:translate-x-full peer" />
                                <label htmlFor="toggle-screen" className="block overflow-hidden h-6 rounded-full bg-surface-hover cursor-pointer border border-white/5 peer-checked:bg-primary"></label>
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                if (checking) return;
                                setChecking(true);
                                const loadingToast = toast.loading(t('settings.checking_updates', '正在检查更新...'));
                                try {
                                    const response = await updateService.getLatestVersion();
                                    if (response.success && response.data) {
                                        setLatestVersion(response.data);
                                        setShowUpdateModal(true);
                                        toast.dismiss(loadingToast);
                                    } else {
                                        toast.error('检查更新失败: ' + response.error, { id: loadingToast });
                                    }
                                } catch (e) {
                                    toast.error(t('settings.check_update_error', '检查更新出错'), { id: loadingToast });
                                } finally {
                                    setChecking(false);
                                }
                            }}
                            disabled={checking}
                            className="w-full flex items-center justify-between p-4 active:bg-surface-hover transition-colors disabled:opacity-70"
                        >
                            <div className="flex items-center gap-3">
                                <span className={`material-icons ${checking ? 'animate-spin' : ''}`}>
                                    {checking ? 'sync' : 'system_update'}
                                </span>
                                <span className="text-base font-medium text-gray-200">{t('settings.check_update', '检查更新')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400 font-mono">v{currentVersion}</span>
                                <span className="material-icons text-gray-500 text-sm">chevron_right</span>
                            </div>
                        </button>
                    </div>
                </section>

                <section>
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 ml-1">{t('settings.device_info', '设备信息')}</h2>
                    <div className="bg-surface-dark rounded-xl border border-white/5 overflow-hidden p-4 flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">{t('settings.device_model', '设备型号')}</span>
                            <span className="text-sm font-medium text-white font-mono">NT20</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">{t('settings.device_id', '设备编号')}</span>
                            <span className="text-sm font-medium text-white font-mono">NT20-001</span>
                        </div>
                    </div>
                </section>

                <div className="mt-4">
                    <button onClick={handleLogout} className="w-full py-4 rounded-xl bg-surface-dark border border-danger/30 text-danger font-bold text-lg hover:bg-danger hover:text-white transition-all active:scale-[0.98] shadow-lg">
                        {t('settings.logout', '退出登录')}
                    </button>
                </div>
            </main >

            {/* Render Modals */}
            {showPrinterModal && <PrinterModal />}
            {showUpdateModal && <UpdateModal />}
            {showPdaModal && <PdaModal />}
            {showStaffModal && <StaffManagementModal />}
            {showSequenceModal && <NumberSequenceModal />}
        </div >
    );
};

export default Settings;
